import React, { useState, useRef, useEffect } from 'react';
import * as XLSX from 'xlsx';
import { AiOutlineCloudUpload, AiOutlineFileExcel, AiOutlineCopy, AiOutlineCheck, AiOutlineDatabase } from 'react-icons/ai';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const Statement01AModule = ({ rowData, onDataChange, extraData }) => {
    const [activeTab, setActiveTab] = useState('GSTR-1');
    const [inwardData, setInwardData] = useState([]);
    const [outwardData, setOutwardData] = useState([]);

    // Build a localStorage key scoped to this client
    const lsKey = `stmt01a_${rowData?.client?.id || rowData?.id || 'default'}`;

    // ── Load saved data from localStorage on mount ──────────────────────────
    useEffect(() => {
        try {
            const saved = localStorage.getItem(lsKey);
            if (saved) {
                const parsed = JSON.parse(saved);
                if (parsed.inwardData) setInwardData(parsed.inwardData);
                if (parsed.outwardData) setOutwardData(parsed.outwardData);
                if (parsed.gstr1Metadata) setGstr1Metadata(parsed.gstr1Metadata);
                if (parsed.gstr2bMetadata) setGstr2bMetadata(parsed.gstr2bMetadata);
                if (parsed.gstr1Stats) setGstr1Stats(parsed.gstr1Stats);
                if (parsed.gstr2bStats) setGstr2bStats(parsed.gstr2bStats);
            }
        } catch (err) {
            console.warn('Statement01A: failed to load from localStorage', err);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lsKey]);

    useEffect(() => {
        if (onDataChange) {
            onDataChange({ inwardData, outwardData });
        }
    }, [inwardData, outwardData, onDataChange]);

    const [gstr1Metadata, setGstr1Metadata] = useState({});
    const [gstr2bMetadata, setGstr2bMetadata] = useState({});
    const [copied, setCopied] = useState(false);
    const [loading, setLoading] = useState(false);

    // Stats bar: rows per sheet
    const [gstr1Stats, setGstr1Stats] = useState({ B2CS: 0, B2CL: 0, B2B: 0, CDN: 0 });
    const [gstr2bStats, setGstr2bStats] = useState({ B2B: 0, CDNR: 0, IMPG: 0, IMPG_SEZ: 0 });

    // Helper: persist everything to localStorage
    const saveToLS = (patch) => {
        try {
            const current = JSON.parse(localStorage.getItem(lsKey) || '{}');
            localStorage.setItem(lsKey, JSON.stringify({ ...current, ...patch }));
        } catch (err) {
            console.warn('Statement01A: failed to save to localStorage', err);
        }
    };

    const gstr1InputRef = useRef(null);
    const gstr2bInputRef = useRef(null);

    // Global Helpers
    const formatDate = (raw) => {
        if (!raw) return "";
        if (typeof raw === "number") {
            const d = new Date(Math.round((raw - 25569) * 86400 * 1000));
            return `${String(d.getUTCDate()).padStart(2, "0")}-${String(d.getUTCMonth() + 1).padStart(2, "0")}-${d.getUTCFullYear()}`;
        }
        const s = String(raw).trim();
        if (s.includes("/")) {
            const [d, m, y] = s.split("/");
            return `${String(d).padStart(2, "0")}-${String(m).padStart(2, "0")}-${y}`;
        }
        if (s.length >= 10 && s[4] === "-") {
            const parts = s.split(/[-T ]/);
            return `${String(parts[2]).substring(0, 2).padStart(2, "0")}-${parts[1]}-${parts[0]}`;
        }
        return s;
    };

    const findSheet = (wb, names) => {
        for (const name of names) {
            const found = wb.SheetNames.find(s =>
                s.trim().toLowerCase() === name.trim().toLowerCase()
            );
            if (found) return wb.Sheets[found];
        }
        return null;
    };

    const formatINR = (val) => {
        const n = parseFloat(val);
        if (isNaN(n) || n === 0) return "";
        return new Intl.NumberFormat("en-IN", {
            minimumFractionDigits: 2, maximumFractionDigits: 2
        }).format(n);
    };

    const safeNum = (v) => {
        const n = parseFloat(v);
        return isNaN(n) ? 0 : n;
    };

    // Parsing GSTR-1
    const handleGstr1Upload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setLoading(true);
        const data = await file.arrayBuffer();
        const wb = XLSX.read(data);

        let allOutward = [];
        let stats = { B2CS: 0, B2CL: 0, B2B: 0, CDN: 0 };
        let metadata = {};

        // Metadata read (row 0,1,2, col 2)
        const firstSheet = wb.Sheets[wb.SheetNames[0]];
        const firstData = XLSX.utils.sheet_to_json(firstSheet, { header: 1, defval: "" });
        if (firstData && firstData.length > 2) {
            metadata.companyName = firstData[0][2];
            metadata.returnPeriod = firstData[1][2];
            metadata.reportName = firstData[2][2];
        }
        setGstr1Metadata(metadata);

        // 1. B2CS
        const b2csSheet = findSheet(wb, ['GSTR-1-1A_B2CS', 'GSTR-1_B2CS', 'B2CS']);
        if (b2csSheet) {
            const rows = XLSX.utils.sheet_to_json(b2csSheet, { header: 1, defval: "" });
            if (rows.length > 5) {
                let sumF = 0, sumG = 0, sumH = 0, sumI = 0;
                for (let i = 5; i < rows.length; i++) {
                    sumF += safeNum(rows[i][5]);
                    sumG += safeNum(rows[i][6]);
                    sumH += safeNum(rows[i][7]);
                    sumI += safeNum(rows[i][8]);
                }
                const docType = sumF < 0 ? "Credit Note" : "Invoice/Bill of Entry";
                allOutward.push({
                    type: "B2C-Small",
                    docType: docType,
                    no: "B2COTH",
                    date: "01-07-2017",
                    taxable: sumF,
                    igst: sumG,
                    cgst: sumH,
                    sgst: sumI
                });
                stats.B2CS = 1;
            }
        }

        // 2. B2CL
        const b2clSheet = findSheet(wb, ['GSTR-1-1A_B2CL', 'GSTR-1_B2CL', 'B2CL']);
        if (b2clSheet) {
            const rows = XLSX.utils.sheet_to_json(b2clSheet, { header: 1, defval: "" });
            if (rows.length > 5) {
                let groups = {};
                for (let i = 5; i < rows.length; i++) {
                    const row = rows[i];
                    const taxable = safeNum(row[8]);
                    const oinum = String(row[4] || "").trim();
                    if (!oinum && taxable === 0) continue; // Skip empty rows

                    if (!oinum) {
                        allOutward.push({
                            type: "B2C-Large", docType: "Invoice/Bill of Entry", no: "",
                            date: formatDate(row[5]), taxable: taxable,
                            igst: safeNum(row[9]), cgst: safeNum(row[10]), sgst: safeNum(row[11])
                        });
                        continue;
                    }
                    if (!groups[oinum]) groups[oinum] = { date: formatDate(row[5]), taxable: 0, igst: 0, cgst: 0, sgst: 0 };
                    groups[oinum].taxable += taxable;
                    groups[oinum].igst += safeNum(row[9]);
                    groups[oinum].cgst += safeNum(row[10]);
                    groups[oinum].sgst += safeNum(row[11]);
                }
                Object.entries(groups).forEach(([no, data]) => {
                    allOutward.push({ type: "B2C-Large", docType: "Invoice/Bill of Entry", no, ...data });
                });
                stats.B2CL = Object.keys(groups).length;
            }
        }

        // 3. B2B
        const b2bSheet = findSheet(wb, ['GSTR-1-1A_B2B', 'GSTR-1_B2B', 'B2B']);
        if (b2bSheet) {
            const rows = XLSX.utils.sheet_to_json(b2bSheet, { header: 1, defval: "" });
            if (rows.length > 5) {
                let groups = {};
                for (let i = 5; i < rows.length; i++) {
                    const row = rows[i];
                    const taxable = safeNum(row[8]);
                    const oinum = String(row[4] || "").trim();
                    if (!oinum && taxable === 0) continue; // Skip empty rows

                    if (!oinum) {
                        allOutward.push({
                            type: "B2B", docType: "Invoice/Bill of Entry", no: "",
                            date: formatDate(row[5]), taxable: taxable,
                            igst: safeNum(row[9]), cgst: safeNum(row[10]), sgst: safeNum(row[11])
                        });
                        continue;
                    }
                    if (!groups[oinum]) groups[oinum] = { date: formatDate(row[5]), taxable: 0, igst: 0, cgst: 0, sgst: 0 };
                    groups[oinum].taxable += taxable;
                    groups[oinum].igst += safeNum(row[9]);
                    groups[oinum].cgst += safeNum(row[10]);
                    groups[oinum].sgst += safeNum(row[11]);
                }
                Object.entries(groups).forEach(([no, data]) => {
                    allOutward.push({ type: "B2B", docType: "Invoice/Bill of Entry", no, ...data });
                });
                stats.B2B = Object.keys(groups).length;
            }
        }

        // 4. CDN
        const cdnSheet = findSheet(wb, ['GSTR-1-1A_CDN', 'GSTR-1_CDN', 'CDN']);
        if (cdnSheet) {
            const rows = XLSX.utils.sheet_to_json(cdnSheet, { header: 1, defval: "" });
            if (rows.length > 5) {
                let count = 0;
                for (let i = 5; i < rows.length; i++) {
                    const row = rows[i];
                    if (!row[6]) continue; // Skip if Note No is empty
                    count++;
                    allOutward.push({
                        type: "B2B",
                        docType: row[4],
                        no: row[6],
                        date: formatDate(row[7]),
                        taxable: safeNum(row[12]),
                        igst: safeNum(row[13]),
                        cgst: safeNum(row[14]),
                        sgst: safeNum(row[15])
                    });
                }
                stats.CDN = count;
            }
        }

        setOutwardData(allOutward);
        setGstr1Stats(stats);
        setLoading(false);
        // ── Persist GSTR-1 data to localStorage ─────────────────────────────
        saveToLS({ outwardData: allOutward, gstr1Stats: stats, gstr1Metadata: metadata });
    };

    // Parsing GSTR-2B
    const handleGstr2bUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setLoading(true);
        const data = await file.arrayBuffer();
        const wb = XLSX.read(data);

        let allInward = [];
        let stats = { B2B: 0, CDNR: 0, IMPG: 0, IMPG_SEZ: 0 };
        let metadata = {};

        const readmeSheet = findSheet(wb, ['Read me', 'README']);
        if (readmeSheet) {
            const rows = XLSX.utils.sheet_to_json(readmeSheet, { header: 1, defval: "" });
            if (rows.length > 6) {
                metadata.financialYear = rows[3][2];
                metadata.taxPeriod = rows[4][2];
                metadata.gstin = rows[5][2];
                metadata.legalName = rows[6][2];
            }
        }
        setGstr2bMetadata(metadata);

        // 1. B2B
        const b2bInSheet = findSheet(wb, ['B2B']);
        if (b2bInSheet) {
            const rows = XLSX.utils.sheet_to_json(b2bInSheet, { header: 1, defval: "" });
            if (rows.length > 6) {
                let count = 0;
                for (let i = 6; i < rows.length; i++) {
                    const row = rows[i];
                    if (!row[2]) continue; // Skip if Invoice No is empty
                    count++;
                    const istype = String(row[7] || "").trim().toLowerCase() === "yes" ? "Inward supplies liable to reverse charge" : "Inward Supply from Registered Person";
                    allInward.push({
                        type: istype,
                        gstin: row[0],
                        docType: "Invoice/Bill of Entry",
                        no: row[2],
                        date: formatDate(row[4]),
                        portCode: "",
                        taxable: Math.abs(safeNum(row[9])),
                        igst: Math.abs(safeNum(row[10])),
                        cgst: Math.abs(safeNum(row[11])),
                        sgst: Math.abs(safeNum(row[12]))
                    });
                }
                stats.B2B = count;
            }
        }

        // 2. B2B-CDNR
        const cdnrInSheet = findSheet(wb, ['B2B-CDNR', 'CDNR']);
        if (cdnrInSheet) {
            const rows = XLSX.utils.sheet_to_json(cdnrInSheet, { header: 1, defval: "" });
            if (rows.length > 6) {
                let count = 0;
                for (let i = 6; i < rows.length; i++) {
                    const row = rows[i];
                    const noteNo = String(row[4] || row[2] || "").trim();
                    if (!noteNo) continue;

                    count++;
                    const istype = String(row[8] || "").trim().toLowerCase() === "yes" ? "Inward supplies liable to reverse charge" : "Inward Supply from Registered Person";
                    allInward.push({
                        type: istype,
                        gstin: row[0],
                        docType: "Credit Note",
                        no: noteNo,
                        date: formatDate(row[5] || row[3]),
                        portCode: "",
                        taxable: Math.abs(safeNum(row[10])),
                        igst: Math.abs(safeNum(row[11])),
                        cgst: Math.abs(safeNum(row[12])),
                        sgst: Math.abs(safeNum(row[13]))
                    });
                }
                stats.CDNR = count;
            }
        }

        // 3. IMPG
        const impgSheet = findSheet(wb, ['IMPG']);
        if (impgSheet) {
            const rows = XLSX.utils.sheet_to_json(impgSheet, { header: 1, defval: "" });
            if (rows.length > 6) {
                let count = 0;
                for (let i = 6; i < rows.length; i++) {
                    const row = rows[i];
                    // Column B (index 1): Port Code
                    // Column C (index 2): Invoice Number / BE No 
                    // Column D (index 3): Date of Invoice
                    // Column E (index 4): Taxable Value
                    // Column F (index 5): Integrated Tax

                    const port = String(row[1] || "").trim();
                    const invNo = String(row[2] || "").trim();
                    if (!invNo) continue;

                    count++;
                    allInward.push({
                        type: "Import of Goods/Supplies from SEZ to DTA",
                        gstin: metadata.gstin || "",
                        docType: "Invoice/Bill of Entry",
                        no: invNo,
                        date: formatDate(row[3]),
                        portCode: port,
                        taxable: safeNum(row[4]),
                        igst: safeNum(row[5]),
                        cgst: 0,
                        sgst: 0
                    });
                }
                stats.IMPG = count;
            }
        }

        // 4. IMPG SEZ
        const impgSezSheet = findSheet(wb, ['IMPG SEZ', 'IMPG_SEZ', 'IMPG-SEZ', 'IMPGSEZ', 'IMPG (SEZ)']);
        if (impgSezSheet) {
            const rows = XLSX.utils.sheet_to_json(impgSezSheet, { header: 1, defval: "" });
            if (rows.length > 6) {
                let count = 0;
                for (let i = 6; i < rows.length; i++) {
                    const row = rows[i];
                    // GSTIN (A/0), Trade (B/1), Port (C/2), BE No (D/3), Date (E/4), Taxable (F/5), IGST (G/6)
                    const gstinSez = String(row[0] || "").trim();
                    const beNo = String(row[3] || "").trim();
                    if (!beNo) continue;

                    count++;
                    allInward.push({
                        type: "Import of Goods/Supplies from SEZ to DTA",
                        gstin: gstinSez || metadata.gstin || "",
                        docType: "Invoice/Bill of Entry",
                        no: beNo,
                        date: formatDate(row[4]),
                        portCode: row[2],
                        taxable: safeNum(row[5]),
                        igst: safeNum(row[6]),
                        cgst: 0,
                        sgst: 0
                    });
                }
                stats.IMPG_SEZ = count;
            }
        }

        setInwardData(allInward);
        setGstr2bStats(stats);
        setLoading(false);
        // ── Persist GSTR-2B data to localStorage ────────────────────────────
        saveToLS({ inwardData: allInward, gstr2bStats: stats, gstr2bMetadata: metadata });
    };

    const handleDownload = async () => {
        const totalInwardTaxable = inwardData.reduce((acc, row) => acc + row.taxable, 0);
        const totalInwardIgst = inwardData.reduce((acc, row) => acc + row.igst, 0);
        const totalInwardCgst = inwardData.reduce((acc, row) => acc + row.cgst, 0);
        const totalInwardSgst = inwardData.reduce((acc, row) => acc + row.sgst, 0);

        const totalOutwardTaxable = outwardData.reduce((acc, row) => acc + row.taxable, 0);
        const totalOutwardIgst = outwardData.reduce((acc, row) => acc + row.igst, 0);
        const totalOutwardCgst = outwardData.reduce((acc, row) => acc + row.cgst, 0);
        const totalOutwardSgst = outwardData.reduce((acc, row) => acc + row.sgst, 0);

        const workbook = new ExcelJS.Workbook();
        const sheet = workbook.addWorksheet('Statement 01A');

        // Styles
        const headerStyle = { font: { bold: true, color: { argb: 'FFFFFFFF' } }, fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF334155' } }, alignment: { horizontal: 'center', vertical: 'middle', wrapText: true }, border: { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } } };
        const inwardHeaderStyle = { font: { bold: true }, fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFFBEB' } }, alignment: { horizontal: 'center' }, border: { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } } };
        const outwardHeaderStyle = { font: { bold: true }, fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFEFF6FF' } }, alignment: { horizontal: 'center' }, border: { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } } };
        const noteStyle = { font: { italic: true, color: { argb: 'FF991B1B' }, size: 9 }, alignment: { wrapText: true } };
        const borderStyle = { border: { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } } };

        // 1. Metadata Rows
        sheet.addRow(["GSTIN*", gstr2bMetadata.gstin || ""]);
        sheet.addRow(["From Return Period*", "", "", "", "", "", "Refund Type: ITC accumulated due to Inverted Tax Structure [clause (ii) of first proviso to section 54(3)] (Amount in Rs)"]);
        sheet.addRow(["To Return Period*", "", "", "", "", "", "", totalInwardTaxable, totalInwardIgst, totalInwardCgst, totalInwardSgst, "", "", "", "", totalOutwardTaxable, totalOutwardIgst, totalOutwardCgst, totalOutwardSgst]);
        sheet.addRow(["***Note:- These sheet at a time can take upto 10,000 rows / entries. Kindly download the latest version of offline utility for preparing and uploading the statement on GST portal."]);

        // Merges for Metadata
        sheet.mergeCells(2, 1, 2, 2); // A2:B2
        sheet.mergeCells(2, 7, 2, 13);
        sheet.mergeCells(3, 1, 3, 2); // A3:B3
        sheet.mergeCells(4, 1, 4, 19);
        sheet.getRow(4).height = 35;
        sheet.getRow(4).getCell(1).style = { ...noteStyle, alignment: { vertical: 'middle', wrapText: true } };

        // 2. Section Headers
        const sectionRow = sheet.addRow(["Sr. No*", "Details of documents of inward supplies received", "", "", "", "", "", "", "Tax paid on inward supplies", "", "", "Details of documents of outward supplies issued", "", "", "", "", "Tax paid on outward supplies"]);
        sectionRow.eachCell((cell, i) => {
            if (i >= 2 && i <= 11) cell.style = inwardHeaderStyle;
            else if (i >= 12) cell.style = outwardHeaderStyle;
            else cell.style = headerStyle;
        });
        sheet.mergeCells(5, 2, 5, 8); // Inward Details
        sheet.mergeCells(5, 12, 5, 16); // Outward Details
        sheet.mergeCells(5, 9, 5, 11); // Inward Tax
        sheet.mergeCells(5, 17, 5, 19); // Outward Tax

        // 3. Main Headers
        const mainHeaders = ["Sr. No*", "Type of Inward Supply*", "GSTIN of Supplier/Self GSTIN*", "Type of Document*", "No./B/E*", "Date* (dd-mm-yyyy)", "Port Code", "Taxable Value*", "Integrated Tax", "Central Tax", "State/Union Territory Tax", "Type of Outward Supply*", "Type of Document*", "No*", "Date* (dd-mm-yyyy)", "Taxable Value*", "Integrated Tax", "Central Tax", "State/Union Territory Tax"];
        const headerRow = sheet.addRow(mainHeaders);
        headerRow.eachCell((cell) => { cell.style = headerStyle; });
        sheet.getRow(6).height = 40;

        // 4. Data Rows
        const maxRows = Math.max(inwardData.length, outwardData.length);
        for (let i = 0; i < maxRows; i++) {
            const inRow = inwardData[i] || {};
            const outRow = outwardData[i] || {};
            const row = sheet.addRow([
                i + 1,
                inRow.type || "", inRow.gstin || "", inRow.docType || "", inRow.no || "", inRow.date || "", inRow.portCode || "", inRow.taxable || 0, inRow.igst || 0, inRow.cgst || 0, inRow.sgst || 0,
                outRow.type || "", outRow.docType || "", outRow.no || "", outRow.date || "", outRow.taxable || 0, outRow.igst || 0, outRow.cgst || 0, outRow.sgst || 0
            ]);
            row.eachCell((cell) => { cell.border = borderStyle.border; });
        }

        // 5. Formatting & Column Widths
        sheet.columns = [
            { width: 8 }, // Sr
            { width: 25 }, { width: 20 }, { width: 18 }, { width: 18 }, { width: 18 }, { width: 12 }, { width: 18 }, { width: 18 }, { width: 18 }, { width: 18 }, // Inward
            { width: 25 }, { width: 18 }, { width: 18 }, { width: 18 }, { width: 18 }, { width: 18 }, { width: 18 }, { width: 18 } // Outward
        ];

        // Number Formatting (Cols 8-11 and 16-19)
        const moneyCols = [8, 9, 10, 11, 16, 17, 18, 19];
        sheet.eachRow((row, rowNumber) => {
            if (rowNumber >= 3) {
                moneyCols.forEach(colIdx => {
                    const cell = row.getCell(colIdx);
                    if (cell.value && typeof cell.value === 'number') {
                        cell.numFmt = '#,##0.00';
                        cell.alignment = { horizontal: 'right' };
                    }
                });
            }
        });

        // Totals Row Formatting (Row 3 specifically)
        const row3Arr = [8, 9, 10, 11, 16, 17, 18, 19];
        row3Arr.forEach(colIdx => {
            const cell = sheet.getRow(3).getCell(colIdx);
            cell.numFmt = '#,##0.00';
            cell.alignment = { horizontal: 'right' };
        });

        const buffer = await workbook.xlsx.writeBuffer();
        saveAs(new Blob([buffer]), `Statement_01A_${gstr2bMetadata.gstin || 'export'}.xlsx`);
    };

    const copyToTSV = () => {
        const data = activeTab === 'GSTR-1' ? outwardData : inwardData;
        if (data.length === 0) return;

        const headers = activeTab === 'GSTR-1'
            ? ['Type', 'DocType', 'InvNo', 'Date', 'Taxable', 'IGST', 'CGST', 'SGST']
            : ['Type', 'GSTIN', 'DocType', 'InvNo', 'Date', 'PortCode', 'Taxable', 'IGST', 'CGST', 'SGST'];

        const rows = data.map(r => activeTab === 'GSTR-1'
            ? [r.type, r.docType, r.no, r.date, r.taxable, r.igst, r.cgst, r.sgst]
            : [r.type, r.gstin, r.docType, r.no, r.date, r.portCode, r.taxable, r.igst, r.cgst, r.sgst]
        );

        const tsv = [headers, ...rows].map(row => row.join('\t')).join('\n');
        navigator.clipboard.writeText(tsv).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };

    const getRowColor = (type) => {
        if (type === 'B2C-Small') return 'rgba(167, 139, 250, 0.1)';
        if (type === 'B2C-Large') return 'rgba(96, 165, 250, 0.1)';
        if (type === 'B2B') return 'rgba(245, 158, 11, 0.1)';
        if (type?.includes('Credit') || type?.includes('CDN')) return 'rgba(251, 113, 133, 0.1)';
        if (type?.includes('Import')) return 'rgba(45, 212, 191, 0.1)';
        return 'transparent';
    };

    const handleJsonDownload = () => {
        const maxRows = Math.max(inwardData.length, outwardData.length);
        const finalRows = [];
        let sequence = 0;
        
        const NUM2 = (v) => {
            if (v === null || v === undefined || v === "") return 0;
            if (v instanceof Date) return 0;
            const s = String(v).replace(/[,\s]/g, "");
            const n = parseFloat(s);
            if (!isFinite(n)) return 0;
            const r = Math.round(n * 100) / 100;
            return r === 0 ? 0 : r;
        };

        const normalizeDate = (v) => {
            if (!v) return "";
            if (v instanceof Date) {
                const dd = String(v.getDate()).padStart(2, "0");
                const mm = String(v.getMonth() + 1).padStart(2, "0");
                return `${dd}-${mm}-${v.getFullYear()}`;
            }
            let s = String(v).trim();
            if (/^\d{4}[-/]\d{2}[-/]\d{2}/.test(s)) {
                const p = s.split(/[-/T ]/);
                return `${p[2].substring(0, 2).padStart(2, "0")}-${p[1].padStart(2, "0")}-${p[0]}`;
            }
            s = s.replace(/\//g, "-");
            if (/^\d{8}$/.test(s)) {
                return `${s.substring(0, 2)}-${s.substring(2, 4)}-${s.substring(4, 8)}`;
            }
            const parts = s.split("-");
            if (parts.length === 3 && parts[2].length === 2) {
                const yr = parseInt(parts[2]);
                parts[2] = (yr > 50 ? "19" : "20") + parts[2];
                return parts.join("-");
            }
            return s;
        };
        
        for (let i = 0; i < maxRows; i++) {
            const inRow = inwardData[i] || {};
            const outRow = outwardData[i] || {};
            
            const hasLeft = Object.keys(inRow).length > 0;
            const hasRight = Object.keys(outRow).length > 0;
            
            if (!hasLeft && !hasRight) continue;
            
            sequence++;
            const obj = { sno: sequence };
            
            if (hasLeft) {
                obj.istype = String(inRow.type || "").trim();
                // If RCM, use our gstin, else supplier gstin
                if (obj.istype === "Inward supplies liable to reverse charge") {
                    obj.stin = String(gstr2bMetadata.gstin || "").trim().toUpperCase();
                } else {
                    obj.stin = String(inRow.gstin || "").trim().toUpperCase();
                }
                obj.idtype = String(inRow.docType || "").trim();
                obj.inum = String(inRow.no || "").trim().replace(/\s+/g, "");
                obj.idt = normalizeDate(inRow.date);
                obj.portcd = String(inRow.portCode || "").trim();
                obj.val = NUM2(inRow.taxable);
                obj.iamt = NUM2(inRow.igst);
                obj.camt = NUM2(inRow.cgst);
                obj.samt = NUM2(inRow.sgst);
            }
            
            if (hasRight) {
                obj.ostype = String(outRow.type || "").trim();
                obj.odtype = String(outRow.docType || "").trim();
                obj.oinum = String(outRow.no || "").trim().replace(/\s+/g, "");
                obj.oidt = normalizeDate(outRow.date);
                obj.oval = NUM2(outRow.taxable);
                obj.oiamt = NUM2(outRow.igst);
                obj.ocamt = NUM2(outRow.cgst);
                obj.osamt = NUM2(outRow.sgst);
            }
            
            finalRows.push(obj);
        }
        
        // Derive fromFp / toFp in mmyyyy format from extraData (From/To period)
        let fromFpVal = "";
        let toFpVal = "";
        if (extraData?.from) {
            const d = extraData.from instanceof Date ? extraData.from : new Date(extraData.from);
            if (!isNaN(d.getTime())) {
                fromFpVal = `${String(d.getMonth() + 1).padStart(2, "0")}${d.getFullYear()}`;
            }
        }
        if (extraData?.to) {
            const d = extraData.to instanceof Date ? extraData.to : new Date(extraData.to);
            if (!isNaN(d.getTime())) {
                toFpVal = `${String(d.getMonth() + 1).padStart(2, "0")}${d.getFullYear()}`;
            }
        }

        const payload = {
            gstin: String(gstr2bMetadata.gstin || "").trim().toUpperCase(),
            fromFp: fromFpVal,
            toFp: toFpVal,
            refundRsn: "INVITC",
            version: "2.0",
            stmt01A: finalRows
        };
        
        const filename = `S01A_${payload.gstin || 'EXPORT'}.json`;
        const json = JSON.stringify(payload);
        const blob = new Blob([json], { type: "application/json" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        a.click();
        URL.revokeObjectURL(url);
    };

    const downloadStatement01APdf = (inData, outData, metaData) => {
        if (inData.length === 0 && outData.length === 0) return;

        const formatINR = (val) => {
            const n = parseFloat(val);
            if (isNaN(n) || n === 0) return '0.00';
            return new Intl.NumberFormat('en-IN', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
            }).format(n);
        };

        const formatPeriod = (dateVal) => {
            if (!dateVal) return "N/A";
            const d = dateVal instanceof Date ? dateVal : new Date(dateVal);
            if (isNaN(d.getTime())) return "N/A";
            return `${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
        };

        const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
        const PAGE_W = doc.internal.pageSize.getWidth();
        const MARGIN = 5;
        const CONTENT_W = PAGE_W - MARGIN * 2;
        let cursorY = MARGIN;

        // Colors
        const COLOR = {
            primary: [81, 25, 137], // #511989
            white: [255, 255, 255],
            lightBg: [248, 250, 252],
            border: [203, 213, 225],
            textDark: [30, 41, 59],
            headerGray: [226, 232, 240]  // #e2e8f0
        };

        // Title Section
        doc.setFillColor(...COLOR.primary);
        doc.rect(MARGIN, cursorY, CONTENT_W, 8, 'F');
        doc.setTextColor(...COLOR.white);
        doc.setFontSize(11);
        doc.setFont('helvetica', 'bold');
        doc.text("Statement 01A", PAGE_W / 2, cursorY + 5.5, { align: 'center' });
        cursorY += 8;

        // Totals calculation
        const inTax = inData.reduce((a, r) => a + (r.taxable || 0), 0);
        const inIgst = inData.reduce((a, r) => a + (r.igst || 0), 0);
        const inCgst = inData.reduce((a, r) => a + (r.cgst || 0), 0);
        const inSgst = inData.reduce((a, r) => a + (r.sgst || 0), 0);
        
        const outTax = outData.reduce((a, r) => a + (r.taxable || 0), 0);
        const outIgst = outData.reduce((a, r) => a + (r.igst || 0), 0);
        const outCgst = outData.reduce((a, r) => a + (r.cgst || 0), 0);
        const outSgst = outData.reduce((a, r) => a + (r.sgst || 0), 0);

        // Meta Info Block
        doc.setFillColor(...COLOR.lightBg);
        doc.rect(MARGIN, cursorY, CONTENT_W, 26, 'F');
        doc.setDrawColor(...COLOR.border);
        doc.rect(MARGIN, cursorY, CONTENT_W, 26, 'S');
        
        doc.setTextColor(...COLOR.textDark);
        doc.setFontSize(8);
        
        // Left Column (headings of Trade name, From Period, To Period, and GSTIN)
        doc.setFont('helvetica', 'bold');
        doc.text("Trade Name:", MARGIN + 4, cursorY + 5);
        doc.setFont('helvetica', 'normal');
        const tradeName = rowData?.client?.firm_name || metaData?.legalName || gstr1Metadata?.companyName || "N/A";
        doc.text(tradeName, MARGIN + 26, cursorY + 5);

        doc.setFont('helvetica', 'bold');
        doc.text("From Period:", MARGIN + 4, cursorY + 10);
        doc.setFont('helvetica', 'normal');
        const fromPeriod = formatPeriod(extraData?.from);
        doc.text(fromPeriod, MARGIN + 26, cursorY + 10);

        doc.setFont('helvetica', 'bold');
        doc.text("To Period:", MARGIN + 4, cursorY + 15);
        doc.setFont('helvetica', 'normal');
        const toPeriod = formatPeriod(extraData?.to);
        doc.text(toPeriod, MARGIN + 26, cursorY + 15);

        doc.setFont('helvetica', 'bold');
        doc.text("GSTIN:", MARGIN + 4, cursorY + 20);
        doc.setFont('helvetica', 'normal');
        const gstin = rowData?.client?.gstin_no || metaData?.gstin || "N/A";
        doc.text(gstin, MARGIN + 26, cursorY + 20);

        // Right Column (Totals summary)
        const col2X = MARGIN + CONTENT_W / 2 + 5;
        
        doc.setFont('helvetica', 'bold');
        doc.text("Inward Section Totals:", col2X, cursorY + 5);
        doc.setFont('helvetica', 'normal');
        doc.text(`IGST: ${formatINR(inIgst)} | CGST: ${formatINR(inCgst)} | SGST: ${formatINR(inSgst)} | Taxable: ${formatINR(inTax)}`, col2X, cursorY + 10);

        doc.setFont('helvetica', 'bold');
        doc.text("Outward Section Totals:", col2X, cursorY + 15);
        doc.setFont('helvetica', 'normal');
        doc.text(`IGST: ${formatINR(outIgst)} | CGST: ${formatINR(outCgst)} | SGST: ${formatINR(outSgst)} | Taxable: ${formatINR(outTax)}`, col2X, cursorY + 20);

        cursorY += 28;

        // Header configuration for autoTable
        const HEADERS_1 = [
            { content: 'Sr.*', rowSpan: 2, styles: { halign: 'center', valign: 'middle' } },
            { content: 'Inward Supplies Received', colSpan: 7, styles: { halign: 'center' } },
            { content: 'Tax Paid (Inward)', colSpan: 3, styles: { halign: 'center' } },
            { content: 'Outward Supplies Issued', colSpan: 5, styles: { halign: 'center' } },
            { content: 'Tax Paid (Outward)', colSpan: 3, styles: { halign: 'center' } }
        ];

        const HEADERS_2 = [
            'Type', 'GSTIN', 'DocType', 'No.', 'Date', 'Port', 'Value', 'IGST', 'CGST', 'SGST',
            'Type', 'DocType', 'No.', 'Date', 'Value', 'IGST', 'CGST', 'SGST'
        ];

        const maxRows = Math.max(inData.length, outData.length);
        const bodyData = [];
        
        for (let i = 0; i < maxRows; i++) {
            const inRow = inData[i] || {};
            const outRow = outData[i] || {};
            bodyData.push([
                i + 1,
                inRow.type || '', inRow.gstin || '', inRow.docType || '', inRow.no || '', inRow.date || '', inRow.portCode || '',
                formatINR(inRow.taxable), formatINR(inRow.igst), formatINR(inRow.cgst), formatINR(inRow.sgst),
                outRow.type || '', outRow.docType || '', outRow.no || '', outRow.date || '',
                formatINR(outRow.taxable), formatINR(outRow.igst), formatINR(outRow.cgst), formatINR(outRow.sgst)
            ]);
        }

        const footData = [[
            'TOTAL', '', '', '', '', '', '',
            formatINR(inTax), formatINR(inIgst), formatINR(inCgst), formatINR(inSgst),
            '', '', '', '',
            formatINR(outTax), formatINR(outIgst), formatINR(outCgst), formatINR(outSgst)
        ]];

        const COL_W = [8, 18, 24, 16, 16, 16, 12, 16, 14, 14, 14, 18, 16, 16, 16, 16, 14, 14, 14];
        const sumOfCOL_W = COL_W.reduce((a, b) => a + b, 0);
        const scaleFactor = CONTENT_W / sumOfCOL_W;
        const colStylesObj = {};
        COL_W.forEach((w, i) => {
            colStylesObj[i] = { 
                cellWidth: w * scaleFactor, 
                halign: [7, 8, 9, 10, 15, 16, 17, 18].includes(i) ? 'right' : (i === 0 ? 'center' : 'left')
            };
        });

        autoTable(doc, {
            startY: cursorY,
            margin: { left: MARGIN, right: MARGIN },
            tableWidth: CONTENT_W,
            head: [HEADERS_1, HEADERS_2],
            body: bodyData,
            foot: footData,
            headStyles: { fillColor: COLOR.headerGray, textColor: COLOR.textDark, fontStyle: 'bold', fontSize: 6.5, lineColor: COLOR.border, lineWidth: 0.1 },
            bodyStyles: { fontSize: 6, textColor: COLOR.textDark, lineColor: COLOR.border, lineWidth: 0.1, overflow: 'linebreak' },
            footStyles: { fillColor: COLOR.primary, textColor: COLOR.white, fontStyle: 'bold', fontSize: 6.5 },
            columnStyles: colStylesObj,
            didDrawPage: (data) => {
                const pageCount = doc.internal.getNumberOfPages();
                const currentPage = doc.internal.getCurrentPageInfo().pageNumber;
                doc.setFontSize(7);
                doc.setTextColor(150);
                doc.text(`Page ${currentPage} of ${pageCount}`, PAGE_W - MARGIN, doc.internal.pageSize.getHeight() - 4, { align: 'right' });
                doc.text(`Statement 01A — GSTIN: ${gstin || ''}`, MARGIN, doc.internal.pageSize.getHeight() - 4, { align: 'left' });
            }
        });

        doc.save(`Statement_01A_${gstin || 'output'}.pdf`);
    };

    return (
        <div className="statement-01a-container">
            <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Syne:wght@700;800&family=DM+Mono:wght@400;500&display=swap');
        
        .statement-01a-container {
          background: #ffffff;
          color: #1e293b;
          font-family: 'DM Mono', monospace;
          padding: 24px;
          border-radius: 12px;
          border: 1px solid #e2e8f0;
          min-height: 600px;
        }
        
        .syne { font-family: 'Syne', sans-serif; }
        
        .top-bar {
          display: flex;
          align-items: center;
          gap: 20px;
          margin-bottom: 24px;
          border-bottom: 1px solid #e2e8f0;
          padding-bottom: 16px;
        }
        
        .tab-btn {
          background: none;
          border: none;
          color: #64748b;
          padding: 8px 16px;
          cursor: pointer;
          font-weight: 500;
          transition: all 0.3s;
          position: relative;
        }
        
        .tab-btn.active {
          color: #d97706;
        }
        
        .tab-btn.active::after {
          content: '';
          position: absolute;
          bottom: -16px;
          left: 0;
          width: 100%;
          height: 2px;
          background: #d97706;
        }
        
        .dot {
          display: inline-block;
          width: 8px;
          height: 8px;
          border-radius: 50%;
          margin-left: 6px;
          border: 1px solid currentColor;
        }
        
        .dot.filled { background: #d97706; }
        
        .btn-download {
          margin-left: auto;
          background: #fffbeb;
          border: 1px solid #d97706;
          color: #d97706;
          padding: 8px 16px;
          border-radius: 6px;
          cursor: pointer;
          transition: all 0.3s;
          display: flex;
          align-items: center;
          gap: 8px;
        }
        
        .btn-download:hover {
          background: #d97706;
          color: #ffffff;
        }
        
        .metadata-card {
          background: #f8fafc;
          border: 1px solid #e2e8f0;
          padding: 16px;
          border-radius: 8px;
          margin-bottom: 20px;
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 16px;
        }
        
        .meta-item label { color: #64748b; font-size: 12px; display: block; margin-bottom: 4px; }
        .meta-item span { color: #1e293b; font-size: 14px; font-weight: 500; }
        
        .upload-zone {
          border: 2px dashed #cbd5e1;
          border-radius: 12px;
          padding: 40px;
          text-align: center;
          cursor: pointer;
          transition: all 0.3s;
          margin-bottom: 20px;
          position: relative;
        }
        
        .upload-zone:hover {
          border-color: #d97706;
          background: rgba(245, 158, 11, 0.05);
        }
        
        .upload-zone.dragover {
          border-color: #d97706;
          animation: pulse 1.5s infinite;
        }
        
        @keyframes pulse {
          0% { box-shadow: 0 0 0 0 rgba(217, 119, 6, 0.4); }
          70% { box-shadow: 0 0 0 10px rgba(217, 119, 6, 0); }
          100% { box-shadow: 0 0 0 0 rgba(217, 119, 6, 0); }
        }
        
        .stats-bar {
          background: #ffffff;
          border: 1px solid #e2e8f0;
          padding: 16px 20px;
          border-radius: 12px;
          margin-bottom: 24px;
          display: flex;
          align-items: center;
          gap: 32px;
          font-size: 14px;
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
        }
        
        .stat-item { color: #64748b; font-weight: 500; display: flex; align-items: center; gap: 8px; }
        .stat-item span { color: #1e293b; font-weight: 700; font-family: 'DM Mono', monospace; background: #f8fafc; padding: 4px 10px; border-radius: 6px; border: 1px solid #e2e8f0; }
        .stat-item.accent span { color: #d97706; background: #fff7ed; border-color: #ffedd5; }
        
        .preview-container {
          background: #ffffff;
          border: 1px solid #e2e8f0;
          border-radius: 8px;
          overflow: hidden;
        }
        
        .table-wrap {
          overflow: auto;
          width: 100%;
        }
        
        table { width: 100%; border-collapse: separate; border-spacing: 0; font-size: 12px; }
        th { background: #f1f5f9; color: #475569; text-align: left; padding: 12px; position: sticky; top: -1px; z-index: 10; border-bottom: 2px solid #e2e8f0; }
        td { padding: 10px 12px; border-bottom: 1px solid #e2e8f0; color: #334155; }
        
        .statement-table th { border-right: 1px solid #e2e8f0; border-bottom: 1px solid #e2e8f0; }
        .statement-table td { border-right: 1px solid #e2e8f0; }

        
        .btn-copy {
          padding: 8px 12px;
          background: #ffffff;
          border: 1px solid #e2e8f0;
          color: #64748b;
          border-radius: 4px;
          cursor: pointer;
          margin: 12px;
          display: flex;
          align-items: center;
          gap: 6px;
          transition: all 0.2s;
        }
        
        .btn-copy:hover { color: #1e293b; border-color: #94a3b8; }
        
        .loader-overlay {
          position: absolute;
          top: 0; left: 0; right: 0; bottom: 0;
          background: rgba(255, 255, 255, 0.8);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 100;
        }
        
        .spinner {
          width: 40px;
          height: 40px;
          border: 3px solid rgba(217, 119, 6, 0.15);
          border-top-color: #d97706;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }
        
        @keyframes spin { to { transform: rotate(360deg); } }
        
        .staggered-row { animation: fadeIn 0.3s ease forwards; opacity: 0; }
        @keyframes fadeIn { from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: translateY(0); } }
      `}</style>

            <div className="top-bar">
                <h2 className="syne" style={{ margin: 0, fontSize: '20px', color: '#1e293b' }}>Statement 01A</h2>
                <button
                    className={`tab-btn ${activeTab === 'GSTR-1' ? 'active' : ''}`}
                    onClick={() => setActiveTab('GSTR-1')}
                >
                    GSTR-1 <span className={`dot ${outwardData.length ? 'filled' : ''}`}></span>
                </button>
                <button
                    className={`tab-btn ${activeTab === 'GSTR-2B' ? 'active' : ''}`}
                    onClick={() => setActiveTab('GSTR-2B')}
                >
                    GSTR-2B <span className={`dot ${inwardData.length ? 'filled' : ''}`}></span>
                </button>
                <button
                    className={`tab-btn ${activeTab === 'Statement-01A' ? 'active' : ''}`}
                    onClick={() => setActiveTab('Statement-01A')}
                >
                    Statement 01A Preview
                </button>
                <button className="btn-download" onClick={handleDownload} disabled={!outwardData.length && !inwardData.length}>
                    ⬇ Download Excel
                </button>
                <button
                    className="btn-download"
                    style={{ background: '#7c3aed', border: '1px solid #7c3aed', color: '#fff', marginLeft: '8px' }}
                    onClick={() => downloadStatement01APdf(inwardData, outwardData, gstr2bMetadata)}
                    disabled={inwardData.length === 0 && outwardData.length === 0}
                >
                    ⬇ Download PDF
                </button>
                <button
                    className="btn-download"
                    style={{ background: '#d97706', color: '#ffffff', marginLeft: '8px' }}
                    onClick={handleJsonDownload}
                    disabled={!outwardData.length && !inwardData.length}
                >
                    JSON Export
                </button>
            </div>

            {activeTab === 'GSTR-1' ? (
                <div key="gstr1-view">
                    <div className="metadata-card">
                        <div className="meta-item">
                            <label>Company Name</label>
                            <span>{gstr1Metadata.companyName || "---"}</span>
                        </div>
                        <div className="meta-item">
                            <label>Return Period</label>
                            <span>{gstr1Metadata.returnPeriod || "---"}</span>
                        </div>
                        <div className="meta-item">
                            <label>Report Name</label>
                            <span>{gstr1Metadata.reportName || "---"}</span>
                        </div>
                    </div>

                    <div className="upload-zone" onClick={() => gstr1InputRef.current?.click()}>
                        <input type="file" ref={gstr1InputRef} hidden accept=".xlsx,.xls" onChange={handleGstr1Upload} />
                        <AiOutlineCloudUpload size={48} color="#d97706" style={{ marginBottom: '12px' }} />
                        <div className="syne" style={{ fontSize: '18px', marginBottom: '4px' }}>Upload GSTR-1 Excel</div>
                        <div style={{ color: '#64748b' }}>Drag-and-drop or click to browse</div>
                        {loading && activeTab === 'GSTR-1' && <div className="loader-overlay"><div className="spinner"></div></div>}
                    </div>

                    <div className="stats-bar">
                        <div className="stat-item">B2CS: <span>{gstr1Stats.B2CS}</span></div>
                        <div className="stat-item">B2CL: <span>{gstr1Stats.B2CL}</span></div>
                        <div className="stat-item">B2B: <span>{gstr1Stats.B2B}</span></div>
                        <div className="stat-item">CDN: <span>{gstr1Stats.CDN}</span></div>
                        <div className="stat-item" style={{ marginLeft: 'auto' }}>Total Rows: <span>{outwardData.length}</span></div>
                    </div>

                    <div className="preview-container">
                        <div className="table-wrap">
                            <table>
                                <thead>
                                    <tr>
                                        <th>Type</th>
                                        <th>DocType</th>
                                        <th>InvNo</th>
                                        <th>Date</th>
                                        <th>Taxable</th>
                                        <th>IGST</th>
                                        <th>CGST</th>
                                        <th>SGST</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {outwardData.map((row, i) => (
                                        <tr key={i} className="staggered-row" style={{ animationDelay: `${i * 0.02}s`, background: getRowColor(row.type) }}>
                                            <td>{row.type}</td>
                                            <td>{row.docType}</td>
                                            <td>{row.no}</td>
                                            <td>{row.date}</td>
                                            <td>{formatINR(row.taxable)}</td>
                                            <td>{formatINR(row.igst)}</td>
                                            <td>{formatINR(row.cgst)}</td>
                                            <td>{formatINR(row.sgst)}</td>
                                        </tr>
                                    ))}
                                    {outwardData.length === 0 && (
                                        <tr><td colSpan="8" style={{ textAlign: 'center', color: '#64748b', padding: '40px' }}>No data imported yet.</td></tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                        <button className="btn-copy" onClick={copyToTSV}>
                            {copied ? <AiOutlineCheck color="#2dd4bf" /> : <AiOutlineCopy />}
                            {copied ? 'Copied ✓' : 'Copy TSV'}
                        </button>
                    </div>
                </div>
            ) : activeTab === 'GSTR-2B' ? (
                <div key="gstr2b-view">
                    <div className="metadata-card">
                        <div className="meta-item">
                            <label>Legal Name</label>
                            <span>{gstr2bMetadata.legalName || "---"}</span>
                        </div>
                        <div className="meta-item">
                            <label>GSTIN</label>
                            <span>{gstr2bMetadata.gstin || "---"}</span>
                        </div>
                        <div className="meta-item">
                            <label>Tax Period</label>
                            <span>{gstr2bMetadata.taxPeriod || "---"}</span>
                        </div>
                        <div className="meta-item">
                            <label>Financial Year</label>
                            <span>{gstr2bMetadata.financialYear || "---"}</span>
                        </div>
                    </div>

                    <div className="upload-zone" onClick={() => gstr2bInputRef.current?.click()}>
                        <input type="file" ref={gstr2bInputRef} hidden accept=".xlsx,.xls" onChange={handleGstr2bUpload} />
                        <AiOutlineCloudUpload size={48} color="#d97706" style={{ marginBottom: '12px' }} />
                        <div className="syne" style={{ fontSize: '18px', marginBottom: '4px' }}>Upload GSTR-2B Excel</div>
                        <div style={{ color: '#64748b' }}>Drag-and-drop or click to browse</div>
                        {loading && activeTab === 'GSTR-2B' && <div className="loader-overlay"><div className="spinner"></div></div>}
                    </div>

                    <div className="stats-bar">
                        <div className="stat-item">B2B: <span>{gstr2bStats.B2B}</span></div>
                        <div className="stat-item">CDNR: <span>{gstr2bStats.CDNR}</span></div>
                        <div className="stat-item">IMPG: <span>{gstr2bStats.IMPG}</span></div>
                        <div className="stat-item">SEZ: <span>{gstr2bStats.IMPG_SEZ}</span></div>
                        <div className="stat-item" style={{ marginLeft: 'auto' }}>Total Rows: <span>{inwardData.length}</span></div>
                    </div>

                    <div className="preview-container">
                        <div className="table-wrap">
                            <table>
                                <thead>
                                    <tr>
                                        <th>Type</th>
                                        <th>GSTIN</th>
                                        <th>DocType</th>
                                        <th>InvNo</th>
                                        <th>Date</th>
                                        <th>PortCode</th>
                                        <th>Taxable</th>
                                        <th>IGST</th>
                                        <th>CGST</th>
                                        <th>SGST</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {inwardData.map((row, i) => (
                                        <tr key={i} className="staggered-row" style={{ animationDelay: `${i * 0.02}s`, background: row.type.includes('reverse') ? 'rgba(251,113,133,0.05)' : 'rgba(245, 158, 11, 0.03)' }}>
                                            <td><div style={{ width: '120px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={row.type}>{row.type}</div></td>
                                            <td>{row.gstin}</td>
                                            <td>{row.docType}</td>
                                            <td>{row.no}</td>
                                            <td>{row.date}</td>
                                            <td>{row.portCode}</td>
                                            <td>{formatINR(row.taxable)}</td>
                                            <td>{formatINR(row.igst)}</td>
                                            <td>{formatINR(row.cgst)}</td>
                                            <td>{formatINR(row.sgst)}</td>
                                        </tr>
                                    ))}
                                    {inwardData.length === 0 && (
                                        <tr><td colSpan="10" style={{ textAlign: 'center', color: '#64748b', padding: '40px' }}>No data imported yet.</td></tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                        <button className="btn-copy" onClick={copyToTSV}>
                            {copied ? <AiOutlineCheck color="#2dd4bf" /> : <AiOutlineCopy />}
                            {copied ? 'Copied ✓' : 'Copy TSV'}
                        </button>
                    </div>
                </div>
            ) : (
                <div key="statement-01a-view">
                    <div className="stats-bar" style={{ background: '#ffffff', border: '1px solid #e2e8f0' }}>
                        <div className="stat-item accent">Inward Taxable: <span>{formatINR(inwardData.reduce((acc, row) => acc + row.taxable, 0))}</span></div>
                        <div className="stat-item accent">Outward Taxable: <span>{formatINR(outwardData.reduce((acc, row) => acc + row.taxable, 0))}</span></div>
                        <div className="stat-item" style={{ marginLeft: 'auto' }}>Total Inward Rows: <span>{inwardData.length}</span></div>
                        <div className="stat-item">Total Outward Rows: <span>{outwardData.length}</span></div>
                    </div>

                    <div className="preview-container" style={{ border: '2px solid #e2e8f0' }}>
                        <div className="table-wrap">
                            <table className="statement-table" style={{ minWidth: '2000px' }}>

                                <thead>
                                    <tr style={{ position: 'sticky', top: 0, zIndex: 20 }}>
                                        <th colSpan="1" style={{ background: '#511989', color: '#ffffff' }}>GSTIN*</th>
                                        <th colSpan="18" style={{ background: '#f8fafc', color: '#1e293b', textAlign: 'left', paddingLeft: '15px' }}>{gstr2bMetadata.gstin || ""}</th>
                                    </tr>
                                    <tr style={{ position: 'sticky', top: '40px', zIndex: 20 }}>
                                        <th colSpan="2" style={{ background: '#511989', color: '#ffffff' }}>From Return Period*</th>
                                        <th colSpan="4" style={{ background: '#ffffff', color: '#1e293b' }}></th>
                                        <th colSpan="13" style={{ background: '#f1f5f9', color: '#475569', fontSize: '11px', fontWeight: '600' }}>Refund Type: ITC accumulated due to Inverted Tax Structure [clause (ii) of first proviso to section 54(3)] (Amount in Rs)</th>
                                    </tr>
                                    <tr style={{ position: 'sticky', top: '80px', zIndex: 20 }}>
                                        <th colSpan="2" style={{ background: '#511989', color: '#ffffff' }}>To Return Period*</th>
                                        <th colSpan="5" style={{ background: '#511989', color: '#ffffff', textAlign: 'right', paddingRight: '20px' }}>Grand Totals (Statement 01A):</th>
                                        <th style={{ background: '#fffbeb', color: '#92400e', fontWeight: '800', borderBottom: '2px solid #d97706' }}>{formatINR(inwardData.reduce((acc, r) => acc + r.taxable, 0))}</th>
                                        <th style={{ background: '#fffbeb', color: '#92400e', fontWeight: '800', borderBottom: '2px solid #d97706' }}>{formatINR(inwardData.reduce((acc, r) => acc + r.igst, 0))}</th>
                                        <th style={{ background: '#fffbeb', color: '#92400e', fontWeight: '800', borderBottom: '2px solid #d97706' }}>{formatINR(inwardData.reduce((acc, r) => acc + r.cgst, 0))}</th>
                                        <th style={{ background: '#fffbeb', color: '#92400e', fontWeight: '800', borderBottom: '2px solid #d97706' }}>{formatINR(inwardData.reduce((acc, r) => acc + r.sgst, 0))}</th>
                                        <th colSpan="4" style={{ background: '#511989' }}></th>
                                        <th style={{ background: '#eff6ff', color: '#1e40af', fontWeight: '800', borderBottom: '2px solid #3b82f6' }}>{formatINR(outwardData.reduce((acc, r) => acc + r.taxable, 0))}</th>
                                        <th style={{ background: '#eff6ff', color: '#1e40af', fontWeight: '800', borderBottom: '2px solid #3b82f6' }}>{formatINR(outwardData.reduce((acc, r) => acc + r.igst, 0))}</th>
                                        <th style={{ background: '#eff6ff', color: '#1e40af', fontWeight: '800', borderBottom: '2px solid #3b82f6' }}>{formatINR(outwardData.reduce((acc, r) => acc + r.cgst, 0))}</th>
                                        <th style={{ background: '#eff6ff', color: '#1e40af', fontWeight: '800', borderBottom: '2px solid #3b82f6' }}>{formatINR(outwardData.reduce((acc, r) => acc + r.sgst, 0))}</th>
                                    </tr>
                                    {/* Row 5 - Structure Headers */}
                                    <tr style={{ textAlign: 'center', background: '#e2e8f0', position: 'sticky', top: '120px', zIndex: 20 }}>
                                        <th rowSpan="2">Sr.*</th>
                                        <th colSpan="7">Inward Supplies Received</th>
                                        <th colSpan="3">Tax Paid (Inward)</th>
                                        <th colSpan="5">Outward Supplies Issued</th>
                                        <th colSpan="3">Tax Paid (Outward)</th>
                                    </tr>
                                    <tr style={{ fontSize: '10px', position: 'sticky', top: '150px', zIndex: 20 }}>
                                        <th>Type</th>
                                        <th>GSTIN</th>
                                        <th>DocType</th>
                                        <th>No.</th>
                                        <th>Date</th>
                                        <th>Port</th>
                                        <th>Value</th>
                                        <th>IGST</th>
                                        <th>CGST</th>
                                        <th>SGST</th>
                                        <th>Type</th>
                                        <th>DocType</th>
                                        <th>No.</th>
                                        <th>Date</th>
                                        <th>Value</th>
                                        <th>IGST</th>
                                        <th>CGST</th>
                                        <th>SGST</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {Array.from({ length: Math.max(inwardData.length, outwardData.length) }).map((_, i) => {
                                        const inRow = inwardData[i] || {};
                                        const outRow = outwardData[i] || {};
                                        return (
                                            <tr key={i} className="staggered-row" style={{ animationDelay: `${i * 0.01}s` }}>
                                                <td style={{ color: '#64748b' }}>{i + 1}</td>
                                                <td title={inRow.type} style={{ maxWidth: '120px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{inRow.type}</td>
                                                <td>{inRow.gstin}</td>
                                                <td title={inRow.docType}>{inRow.docType}</td>
                                                <td>{inRow.no}</td>
                                                <td>{inRow.date}</td>
                                                <td>{inRow.portCode}</td>
                                                <td style={{ color: '#b45309', fontWeight: 'bold' }}>{formatINR(inRow.taxable)}</td>
                                                <td>{formatINR(inRow.igst)}</td>
                                                <td>{formatINR(inRow.cgst)}</td>
                                                <td>{formatINR(inRow.sgst)}</td>
                                                <td title={outRow.type} style={{ maxWidth: '120px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{outRow.type}</td>
                                                <td title={outRow.docType}>{outRow.docType}</td>
                                                <td>{outRow.no}</td>
                                                <td>{outRow.date}</td>
                                                <td style={{ color: '#2563eb', fontWeight: 'bold' }}>{formatINR(outRow.taxable)}</td>
                                                <td>{formatINR(outRow.igst)}</td>
                                                <td>{formatINR(outRow.cgst)}</td>
                                                <td>{formatINR(outRow.sgst)}</td>
                                            </tr>
                                        );
                                    })}
                                    {inwardData.length === 0 && outwardData.length === 0 && (
                                        <tr><td colSpan="19" style={{ textAlign: 'center', color: '#64748b', padding: '40px' }}>Please upload files in GSTR-1 and GSTR-2B tabs first.</td></tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}
        </div >
    );
};

export default Statement01AModule;
